import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import { Button } from '@ui/buttons';
import {
  getNodonSnackbarPillStyles,
  NODON_SNACKBAR_COLOR,
  NODON_SNACKBAR_SURFACE,
} from '@client/style/nodon-snackbar.styles';
import type { Coordinate } from '@shared/models/geography.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { DesignMap } from '../map';
import { isBuildingOutsideMapViewport } from '../helpers/building-viewport.helpers';

type BuildingOutOfViewBarProps = {
  map: DesignMap | undefined;
  location: Coordinate | undefined;
  storeys: Storey[];
};

/**
 * Canvas toast when the site footprint leaves the viewport.
 * Same solid pill + text action as app snackbars ({@link NodonSnackbarProvider}).
 * {@link BuildingOutOfViewBarProps.storeys} should include every building.
 */
export const BuildingOutOfViewBar: React.FC<BuildingOutOfViewBarProps> = ({
  map,
  location,
  storeys,
}) => {
  const { classes } = useStyles();
  const [outOfView, setOutOfView] = useState(false);

  const recompute = useCallback(() => {
    const mapbox = map?.map;
    const bounds = mapbox?.getBounds();
    if (!mapbox || !location || !bounds) {
      setOutOfView(false);
      return;
    }
    setOutOfView(isBuildingOutsideMapViewport(location, storeys, bounds));
  }, [location, map, storeys]);

  useEffect(() => {
    const mapbox = map?.map;
    if (!mapbox) return;
    recompute();
    mapbox.on('moveend', recompute);
    mapbox.on('zoomend', recompute);
    mapbox.on('resize', recompute);
    return () => {
      mapbox.off('moveend', recompute);
      mapbox.off('zoomend', recompute);
      mapbox.off('resize', recompute);
    };
  }, [map, recompute]);

  // Storey/location edits can move the footprint without a camera event.
  useEffect(() => {
    recompute();
  }, [recompute]);

  const handleGoToBuilding = () => {
    map?.flyToBuildingFocus(storeys);
  };

  if (!outOfView) return null;

  return (
    <div className={classes.host} role="status">
      <div className={classes.pill}>
        <Typography variant="body2" className={classes.label}>
          Building is out of view
        </Typography>
        <Button
          variant="text"
          size="small"
          textTransform="none"
          onClick={handleGoToBuilding}
        >
          Show
        </Button>
      </div>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  host: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    zIndex: 5,
    transform: 'translateX(-50%)',
    maxWidth: 'min(520px, calc(100% - 32px))',
    pointerEvents: 'none',
  },
  pill: {
    ...getNodonSnackbarPillStyles(),
    pointerEvents: 'auto',
  },
  label: {
    whiteSpace: 'nowrap',
    textAlign: 'left',
    fontWeight: NODON_SNACKBAR_SURFACE.fontWeight,
    fontSize: NODON_SNACKBAR_SURFACE.fontSize,
    lineHeight: NODON_SNACKBAR_SURFACE.lineHeight,
    color: NODON_SNACKBAR_COLOR.default,
  },
}));
